import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Package, Truck, Clock, Search, RefreshCw, CheckCircle, FileText, ExternalLink } from 'lucide-react';
import prodApi from '../../services/prodApi';

const PendientesRecibir = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await prodApi.get('/inventario/pendientes-recibir');
      setItems(response.data || []);
    } catch (error) {
      toast.error('Error al cargar pendientes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredItems = items.filter(item => 
    item.articulo?.toLowerCase().includes(search.toLowerCase()) ||
    item.proveedor?.toLowerCase().includes(search.toLowerCase()) ||
    item.documento?.toLowerCase().includes(search.toLowerCase())
  );

  // Agrupar por proveedor para el resumen
  const porProveedor = items.reduce((acc, item) => {
    const prov = item.proveedor || 'Sin proveedor';
    if (!acc[prov]) acc[prov] = { cantidad: 0, valor: 0 };
    acc[prov].cantidad += item.cantidad_pendiente;
    acc[prov].valor += item.cantidad_pendiente * item.precio_unitario;
    return acc;
  }, {});

  const totalPendiente = items.reduce((sum, i) => sum + i.cantidad_pendiente, 0);
  const valorTotal = items.reduce((sum, i) => sum + (i.cantidad_pendiente * i.precio_unitario), 0);

  return (
    <div className="space-y-6" data-testid="pendientes-recibir-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Truck className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pendientes de Recibir</h1>
            <p className="text-sm text-gray-500">Artículos por ingresar desde facturas de proveedor</p>
          </div>
        </div>
        <Button onClick={fetchData} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-medium">Total Items</p>
                <p className="text-2xl font-bold text-orange-700">{items.length}</p>
              </div>
              <Package className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Cantidad Total</p>
                <p className="text-2xl font-bold text-blue-700">{totalPendiente.toLocaleString()}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 font-medium">Valor Estimado</p>
                <p className="text-2xl font-bold text-emerald-700">S/ {valorTotal.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">Proveedores</p>
                <p className="text-2xl font-bold text-purple-700">{Object.keys(porProveedor).length}</p>
              </div>
              <Truck className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Búsqueda */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Buscar por artículo, proveedor o documento..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tabla */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5" />
            Detalle de Pendientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-500 mt-2">Cargando...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <CheckCircle className="w-12 h-12 mx-auto text-emerald-400" />
              <p className="text-gray-600 mt-2 font-medium">No hay artículos pendientes de recibir</p>
              <p className="text-gray-400 text-sm">Todas las facturas tienen recepción completa</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Documento</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Artículo</TableHead>
                  <TableHead className="text-right">Pedido</TableHead>
                  <TableHead className="text-right">Recibido</TableHead>
                  <TableHead className="text-right">Pendiente</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item, idx) => (
                  <TableRow key={idx} className="hover:bg-orange-50/50">
                    <TableCell>
                      <Link 
                        to={`/finanzas/compras/${item.compra_id}/editar?tipo=FACTURA_PROVEEDOR`}
                        className="flex items-center gap-1 font-mono font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        title="Ver factura"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        {item.documento}
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </Link>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {item.fecha_factura ? new Date(item.fecha_factura).toLocaleDateString('es-PE') : '-'}
                    </TableCell>
                    <TableCell>{item.proveedor}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{item.articulo}</p>
                        {item.codigo_articulo && (
                          <p className="text-xs text-gray-500 font-mono">{item.codigo_articulo}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{item.cantidad_total}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-600">{item.cantidad_recibida}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-orange-600">{item.cantidad_pendiente}</TableCell>
                    <TableCell>
                      <Badge className={item.estado_recepcion === 'PARCIAL' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}>
                        {item.estado_recepcion}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Resumen por proveedor */}
      {Object.keys(porProveedor).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Resumen por Proveedor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Object.entries(porProveedor).map(([proveedor, data]) => (
                <div key={proveedor} className="p-4 bg-gray-50 rounded-lg border">
                  <p className="font-medium text-gray-800 truncate" title={proveedor}>{proveedor}</p>
                  <div className="flex justify-between mt-2 text-sm">
                    <span className="text-gray-500">Cantidad:</span>
                    <span className="font-mono font-medium">{data.cantidad.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Valor:</span>
                    <span className="font-mono font-medium text-emerald-600">S/ {data.valor.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PendientesRecibir;
